import React from 'react';
import { useNavigate } from 'react-router-dom';
import './Legal.css';

const Terms: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="legal-page">
      <div className="legal-container">
        <button className="back-btn" onClick={() => navigate('/')}>
          ← Back to Home
        </button>

        <h1>Terms of Service</h1>
        <p className="last-updated">Last Updated: November 22, 2025</p>

        <section className="legal-section">
          <h2>1. Agreement to Terms</h2>
          <p>
            By accessing or using Tiny Little Fox Studio ("Service," "Platform," "we," "us," or "our"), you agree to be bound 
            by these Terms of Service ("Terms"). If you disagree with any part of these terms, you may not access the Service.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Description of Service</h2>
          <p>
            Tiny Little Fox Studio is an AI-powered platform that enables users to create, design, and publish illustrated storybooks. 
            Our services include:
          </p>
          <ul>
            <li>AI-generated character illustrations and scenes</li>
            <li>Story planning and plot generation tools</li>
            <li>Visual drag-and-drop editor for page design</li>
            <li>Export capabilities for publishing on platforms like Amazon KDP</li>
            <li>Cloud storage for your created storybooks</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>3. User Accounts</h2>
          <h3>3.1 Account Registration</h3>
          <p>
            To use certain features of the Service, you must register for an account. You agree to provide accurate, current, 
            and complete information during registration and to update such information to keep it accurate, current, and complete.
          </p>

          <h3>3.2 Account Security</h3>
          <p>
            You are responsible for safeguarding your password and for all activities that occur under your account. 
            You agree to notify us immediately of any unauthorized use of your account.
          </p>

          <h3>3.3 Account Termination</h3>
          <p>
            We reserve the right to suspend or terminate your account at any time for violation of these Terms or for any other reason 
            at our sole discretion.
          </p>
        </section>

        <section className="legal-section">
          <h2>4. Intellectual Property and Content Ownership</h2>
          <h3>4.1 Your Content</h3>
          <p>
            You retain full ownership and all intellectual property rights to the storybooks, characters, and content you create 
            using our platform. We do not claim any ownership of your creations.
          </p>

          <h3>4.2 License to Use Service</h3>
          <p>
            By using our Service, you grant us a limited, non-exclusive, royalty-free license to store, display, and process your 
            content solely for the purpose of providing and improving our services to you.
          </p>

          <h3>4.3 Our Intellectual Property</h3>
          <p>
            The Service and its original content (excluding user-generated content), features, and functionality are owned by 
            Tiny Little Fox Studio and are protected by international copyright, trademark, and other intellectual property laws.
          </p>
        </section>

        <section className="legal-section">
          <h2>5. AI-Generated Content</h2>
          <h3>5.1 AI Usage</h3>
          <p>
            Our Service uses third-party AI technologies (including OpenAI and Google) to generate illustrations and text. 
            The quality and appropriateness of AI-generated content may vary.
          </p>

          <h3>5.2 Content Review Responsibility</h3>
          <p>
            You are responsible for reviewing all AI-generated content before use or publication. We do not guarantee that 
            AI-generated content will be accurate, appropriate, or free from errors or biases.
          </p>

          <h3>5.3 Copyright of AI Content</h3>
          <p>
            You retain rights to AI-generated content created through our platform. However, you acknowledge that AI-generated 
            content may have limitations regarding copyright protection under current laws. We recommend consulting with a legal 
            professional if you have concerns about copyright protection for AI-generated works.
          </p>
        </section>

        <section className="legal-section">
          <h2>6. Acceptable Use Policy</h2>
          <p>You agree not to use the Service to:</p>
          <ul>
            <li>Create content that is illegal, harmful, threatening, abusive, harassing, defamatory, or otherwise objectionable</li>
            <li>Generate content that infringes on the intellectual property rights of others</li>
            <li>Create content that is sexually explicit, violent, or promotes discrimination or hate speech</li>
            <li>Generate content involving minors in any inappropriate manner</li>
            <li>Attempt to reverse engineer, decompile, or access our source code</li>
            <li>Use automated scripts or bots to abuse our AI generation services</li>
            <li>Share your account credentials with others</li>
            <li>Resell or redistribute our services without authorization</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>7. Payment and Billing</h2>
          <h3>7.1 Pricing</h3>
          <p>
            Certain features of the Service require payment. Pricing is available on our website and may be changed at any time 
            with notice to users.
          </p>

          <h3>7.2 Payment Processing</h3>
          <p>
            Payments are processed through secure third-party payment processors. We do not store your complete payment card information.
          </p>

          <h3>7.3 AI Token Usage</h3>
          <p>
            AI-powered features may consume tokens or credits. You are responsible for monitoring your usage and purchasing 
            additional tokens as needed. Token pricing is based on our costs for third-party AI services.
          </p>

          <h3>7.4 Refunds</h3>
          <p>
            Due to the nature of digital services and AI token consumption, all sales are generally final. Refunds may be 
            considered on a case-by-case basis for technical issues or service outages.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. Content Moderation</h2>
          <p>
            We reserve the right to review, monitor, and remove content that violates these Terms or is otherwise objectionable. 
            We employ automated and manual content moderation systems, but we do not guarantee that all inappropriate content 
            will be detected or removed.
          </p>
        </section>

        <section className="legal-section">
          <h2>9. Publishing and Third-Party Platforms</h2>
          <p>
            While we provide tools to export your storybooks for publishing on platforms like Amazon KDP, we are not responsible for:
          </p>
          <ul>
            <li>The approval or rejection of your books by third-party platforms</li>
            <li>Compliance with third-party platform requirements</li>
            <li>Sales, royalties, or earnings from third-party platforms</li>
            <li>Copyright disputes or intellectual property issues on third-party platforms</li>
          </ul>
          <p>
            You are solely responsible for ensuring your content meets the requirements of any platform where you choose to publish.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. Disclaimer of Warranties</h2>
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, 
            INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
          </p>
          <p>
            We do not warrant that:
          </p>
          <ul>
            <li>The Service will be uninterrupted, secure, or error-free</li>
            <li>AI-generated content will meet your expectations or requirements</li>
            <li>Any errors or defects will be corrected</li>
            <li>The Service will be compatible with all devices or browsers</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>11. Limitation of Liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, TINY LITTLE FOX STUDIO SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
            SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, OR OTHER 
            INTANGIBLE LOSSES, RESULTING FROM:
          </p>
          <ul>
            <li>Your use or inability to use the Service</li>
            <li>Any unauthorized access to or use of our servers and/or any personal information stored therein</li>
            <li>Any interruption or cessation of transmission to or from the Service</li>
            <li>Any bugs, viruses, or similar harmful components transmitted through the Service</li>
            <li>Any errors or omissions in any content or for any loss or damage incurred as a result of the use of any content</li>
            <li>Quality or appropriateness of AI-generated content</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>12. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless Tiny Little Fox Studio, its officers, directors, employees, and agents 
            from and against any claims, liabilities, damages, losses, and expenses, including reasonable attorneys' fees, arising out of or in any way connected with:
          </p>
          <ul>
            <li>Your access to or use of the Service</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any third-party rights, including intellectual property rights</li>
            <li>Any content you create or publish using our Service</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>13. Data Backup</h2>
          <p>
            While we implement backup systems, you are solely responsible for maintaining backups of your content. 
            We recommend regularly exporting and saving your storybooks locally.
          </p>
        </section>

        <section className="legal-section">
          <h2>14. Service Modifications and Availability</h2>
          <p>
            We reserve the right to modify, suspend, or discontinue the Service (or any part thereof) at any time with or without notice. 
            We shall not be liable to you or any third party for any modification, suspension, or discontinuation of the Service.
          </p>
        </section>

        <section className="legal-section">
          <h2>15. Governing Law</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the United States, 
            without regard to its conflict of law provisions.
          </p>
        </section>

        <section className="legal-section">
          <h2>16. Dispute Resolution</h2>
          <p>
            Any disputes arising out of or relating to these Terms or the Service shall be resolved through binding arbitration, 
            except that either party may seek injunctive relief in court for intellectual property disputes.
          </p>
        </section>

        <section className="legal-section">
          <h2>17. Changes to Terms</h2>
          <p>
            We reserve the right to modify these Terms at any time. We will notify users of any material changes by posting 
            the new Terms on this page and updating the "Last Updated" date. Your continued use of the Service after such 
            modifications constitutes your acceptance of the updated Terms.
          </p>
        </section>

        <section className="legal-section">
          <h2>18. Severability</h2>
          <p>
            If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions shall continue 
            in full force and effect.
          </p>
        </section>

        <section className="legal-section">
          <h2>19. Entire Agreement</h2>
          <p>
            These Terms constitute the entire agreement between you and Tiny Little Fox Studio regarding the use of the Service 
            and supersede any prior agreements.
          </p>
        </section>

        <section className="legal-section">
          <h2>20. Contact Information</h2>
          <p>
            If you have any questions about these Terms, please contact us at:
          </p>
          <p className="contact-info">
            <strong>Email:</strong> legal@tinylittlefox.com<br />
            <strong>Website:</strong> https://tinylittlefox.com
          </p>
        </section>

        <div className="legal-footer">
          <p>
            By using Tiny Little Fox Studio, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Terms;
